import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { type ActivityInterface } from "@via/utils/interfaces.tsx";
import { createFilterQuery } from "@via/pages/activity/utils/helper.ts";

export const fetchRecentActivity = async (): Promise<ActivityInterface> => {
  try {
    const res = await axios.get("/api/user/v1/activity");
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useRecentActivity = (): UseQueryResult<ActivityInterface> => {
  return useQuery<ActivityInterface>({
    queryKey: ["/api/user/v1/activity"],
    queryFn: async () => await fetchRecentActivity(),
    retry: false,
  });
};

export const useActivityRunList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      page: number;
      pageSize: number;
      sort: Array<{ id: string; desc: boolean }>;
      searchKey: string;
      filter: Array<{ id: string; value: string }>;
    }) => {
      const baseUrl = "/api/run/v1/list";
      const { page, pageSize, sort, searchKey, filter } = payload;

      const skip = page * pageSize;
      const take = pageSize;

      const tagIds = filter.filter(f => f.id === "tag").map(f => f.value);
      const nonTagFilters = filter.filter(f => f.id !== "tag");

      const queryParams = new URLSearchParams({
        take: `${take}`,
        skip: `${skip}`,
      });

      if (sort.length > 0) {
        queryParams.append("sort", sort[0].id);
        queryParams.append("order", sort[0].desc ? "desc" : "asc");
      }
      if (nonTagFilters.length > 0) {
        const queryFilter = createFilterQuery(nonTagFilters);
        if (queryFilter) {
          queryParams.append("filter", queryFilter);
        }
      }
      const url = `${baseUrl}?${queryParams.toString()}`;
      const body: { searchKey: string; tagIds?: string[] } = { searchKey };
      if (tagIds.length > 0) {
        body.tagIds = tagIds;
      }
      const res = await axios.post(url, body);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/run/v1/list"] });
    },
    onError: error => {
      throw error;
    },
  });
};
